import React from 'react'
import PropTypes from 'prop-types'
import { get, isEqual } from 'lodash'
import { compose, graphql } from 'react-apollo'
import gql from 'graphql-tag'
import { withLoader } from 'evergreen-draft'
import { Provider, UserContext } from './context'

function identifyUser(user: UserProviderProps['user']): void {
  if (user && window.analytics) {
    window.analytics.identify(user.id, {
      name: user.name,
      email: user.email
    })
  }
}

interface UserProviderProps extends UserContext {}
interface UserProviderState {
  value: UserContext
}

class UserProvider extends React.Component<UserProviderProps, UserProviderState> {
  static propTypes = {
    refreshUser: PropTypes.func,
    user: PropTypes.shape({
      id: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      isAdmin: PropTypes.bool,
      isOperator: PropTypes.bool,
      isAuthorizedViaSSO: PropTypes.bool,
      isAutoprovisionedForSSO: PropTypes.bool,
      verification: PropTypes.string.isRequired,
      pendingEmail: PropTypes.string
    })
  }

  static defaultProps = {
    refreshUser: () => {
      return
    }
  }

  static getDerivedStateFromProps({ user, refreshUser }: UserProviderProps, state: UserProviderState) {
    if (isEqual(user, state.value.user)) {
      return null
    }

    return {
      value: {
        user,
        refreshUser
      }
    }
  }

  /**
   * Use `state.value` for context to avoid unnecessary re-renders in consumers
   * @see {@link https://reactjs.org/docs/context.html#caveats}
   */
  state = {
    value: {
      user: this.props.user,
      refreshUser: this.props.refreshUser
    }
  }

  componentDidMount() {
    identifyUser(this.props.user)
  }

  componentDidUpdate(prevProps: UserProviderProps) {
    if (
      get(prevProps, 'user.id') !== get(this.props, 'user.id') ||
      get(prevProps, 'user.name') !== get(this.props, 'user.name') ||
      get(prevProps, 'user.email') !== get(this.props, 'user.email')
    ) {
      identifyUser(this.props.user)
    }
  }

  render() {
    return <Provider value={this.state.value}>{this.props.children}</Provider>
  }
}

export interface Query {
  me: User | null
  isAuthorizedViaSSO: boolean | null
  ssoAuthorizedWorkspace: Workspace | null
}

export interface User {
  id: string
  email: string
  name: string | null
  isAdmin: boolean | null
  isOperator: boolean | null
  isAutoprovisionedForSSO: boolean | null
  verification: string | null
  pendingEmail: string | null
}

export interface Workspace {
  id: string
  slug: string
  name: string
}

interface OutputProps extends UserContext {
  isLoading: boolean
}

const withUserInfo = graphql<{}, Query, {}, OutputProps>(
  gql`
    query getUserInfo {
      me {
        id
        email
        name
        isAdmin
        isOperator
        isAutoprovisionedForSSO
        verification
        pendingEmail
      }
      isAuthorizedViaSSO
      ssoAuthorizedWorkspace {
        id
        slug
        name
      }
    }
  `,
  {
    props: ({ data }) => {
      let user: UserContext['user'] = null

      // Only if there's a currently logged in user should we decorate it with this bool
      if (data?.me) {
        user = {
          ...data.me,
          isAuthorizedViaSSO: data.isAuthorizedViaSSO ?? null,
          ssoAuthorizedWorkspace: data.ssoAuthorizedWorkspace ?? null
        }
      }

      return {
        refreshUser: data!.refetch,
        isLoading: data!.loading,
        // If there's an error fetching session (which can sometimes be a 401 for invalid token)
        // we still need to be able to let users get to the login page.
        // This ensures we treat fetch errors the same as "no session" by passing a `null` user
        user: data!.error ? null : user
      }
    }
  }
)

export default compose(withUserInfo, withLoader)(UserProvider)
